import { dataService } from '../apis/dataService';
import type { BalanceSheet, FinancialDataQueryParams } from '../../types/api';

/**
 * Parse date string (handles YYYYMM format and standard date formats)
 * @param dateStr - Date string in YYYYMM format or standard date format
 * @returns Date object or null if invalid
 */
function parseDate(dateStr: string): Date | null {
  if (!dateStr) {
    return null;
  }

  // Handle YYYYMM format (e.g., "202401")
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
    return new Date(year, month, 1);
  }

  // Handle standard date formats
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Calculate number of years held based on balance sheet date range
 * @param balanceSheetData - Array of BalanceSheet data
 * @returns Number of years held (difference between min and max dates)
 */
export function calculateYearsHeld(balanceSheetData: BalanceSheet[]): number {
  if (!balanceSheetData || balanceSheetData.length === 0) {
    return 0;
  }

  // Extract all valid dates
  const dates = balanceSheetData
    .map((item) => parseDate(item.calendar_date))
    .filter((date): date is Date => date !== null);

  if (dates.length === 0) {
    return 0;
  }

  // Find min and max dates
  const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

  // Calculate years difference
  const yearsDiff = (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  
  // Round to 1 decimal place
  return Math.round(yearsDiff * 10) / 10;
}

/**
 * Fetch balance sheet data and calculate years held for a specific unit
 * @param user_id_no - The user ID number
 * @param unit_id_no - The unit ID number
 * @returns Promise<number> - Number of years held
 */
export async function fetchYearsHeldForUnit(
  user_id_no: number,
  unit_id_no: number
): Promise<number> {
  if (!user_id_no || !unit_id_no) {
    return 0;
  }

  try {
    const params: FinancialDataQueryParams = {
      user_id_no,
      unit_id_no
    };

    const balanceSheetData = await dataService.getBalanceSheet(params);
    return calculateYearsHeld(balanceSheetData);
  } catch (error) {
    console.error(`Error fetching years held for unit ${unit_id_no}:`, error);
    return 0;
  }
}

/**
 * Format years held as a string
 * @param years - Number of years
 * @returns Formatted string (e.g., "5.2 years" or "0 years")
 */
export function formatYearsHeld(years: number): string {
  if (years === 0) {
    return '0 years';
  }
  
  // Format with 1 decimal place
  return `${years.toFixed(1)} years`;
}

